import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/movies";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [notice, setNotice] = useState(false);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const total = heroSlides.length;

  const goTo = useCallback((i: number) => {
    setIndex((i + total) % total);
  }, [total]);

  const next = useCallback(() => setIndex((v) => (v + 1) % total), [total]);
  const prev = useCallback(() => setIndex((v) => (v - 1 + total) % total), [total]);

  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next, index]);

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(false), 2600);
    return () => clearTimeout(timer);
  }, [notice]);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.changedTouches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    touchEndX.current = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) next();
      else prev();
    }
  };

  const slide = heroSlides[index];

  return (
    <section className="relative w-full h-[440px] md:h-[600px] overflow-hidden bg-background-100">
      {heroSlides.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100 z-10" : "opacity-0 z-0"
          }`}
        >
          <img
            src={item.backdrop}
            alt={`${item.title} 剧照`}
            title={`${item.title} - 蓝莓视频高清影视资源`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 z-20 bg-gradient-to-r from-background-50 via-background-50/70 to-transparent"></div>
      <div className="absolute inset-0 z-20 bg-gradient-to-t from-background-50 via-transparent to-background-50/40"></div>

      <div className="relative z-30 h-full px-4 md:px-8 flex items-center">
        <div className="w-full max-w-2xl">
          <span className="inline-flex items-center gap-2 text-accent-500 text-sm font-medium mb-4">
            <i className="ri-fire-fill"></i>
            {slide.subtitle}
          </span>
          <h1
            key={slide.id}
            className="font-heading font-bold text-4xl md:text-6xl text-foreground-950 mb-4 animate-fade-up"
          >
            {slide.title}
          </h1>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {slide.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs text-foreground-800 bg-background-100/80 border border-background-200/70 px-2.5 py-1 rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
          <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-7 max-w-xl line-clamp-3 md:line-clamp-none">
            {slide.description}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setNotice(true)}
              className="flex items-center gap-2 px-6 py-3 rounded-md bg-accent-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
            >
              <i className="ri-play-fill text-lg"></i>
              立即观看
            </button>
            <Link
              to={`/detail/${slide.movieId}`}
              className="flex items-center gap-2 px-6 py-3 rounded-md bg-background-100 border border-background-200 text-foreground-900 text-sm font-medium whitespace-nowrap hover:border-primary-400 transition-colors cursor-pointer"
            >
              <i className="ri-information-line text-lg"></i>
              查看详情
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={prev}
        aria-label="上一张"
        className="hidden md:flex absolute z-30 left-6 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-background-50/60 text-foreground-950 hover:bg-background-50 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={next}
        aria-label="下一张"
        className="hidden md:flex absolute z-30 right-6 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-background-50/60 text-foreground-950 hover:bg-background-50 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute z-30 bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {heroSlides.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 张`}
            onClick={() => goTo(i)}
            className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
              i === index ? "w-8 bg-accent-500" : "w-3 bg-background-300"
            }`}
          ></button>
        ))}
      </div>

      {notice && (
        <div className="absolute z-40 bottom-20 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-background-100 border border-background-300 text-foreground-900 text-sm px-4 py-3 rounded-lg animate-fade-in">
          <i className="ri-information-line text-accent-500 text-lg"></i>
          演示站点暂不提供真实播放，敬请期待正版观看体验
        </div>
      )}
    </section>
  );
}